/**
 * EcommerceSettings
 * Configuration de la boutique E-Commerce
 * - Activation/Désactivation
 * - Accès direct au site client
 * - Apparence (logo, couleurs, nom)
 * - Options de livraison et paiement
 */

import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';

/** 0 = dimanche … 6 = samedi, comme JS getDay() et comme le serveur. */
const JOURS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const HEURES = Array.from({ length: 24 }, (_, h) => h);

export default function EcommerceSettings() {
  const { tenant, reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // États du formulaire
  const [ecommerceEnabled, setEcommerceEnabled] = useState(tenant?.config?.ecommerce?.enabled ?? true);
  const [storeName, setStoreName] = useState(tenant?.config?.ecommerce?.storeName || tenant?.name || '');
  const [storeDescription, setStoreDescription] = useState(tenant?.config?.ecommerce?.storeDescription || '');
  const [primaryColor, setPrimaryColor] = useState(tenant?.config?.ecommerce?.primaryColor || '#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState(tenant?.config?.ecommerce?.secondaryColor || '#10B981');
  const [accentColor, setAccentColor] = useState(tenant?.config?.ecommerce?.accentColor || '#F59E0B');

  // Logo
  const [logoPreview, setLogoPreview] = useState<string | null>(tenant?.config?.ecommerce?.logo || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Options de livraison
  const [deliveryEnabled, setDeliveryEnabled] = useState(tenant?.config?.ecommerce?.deliveryEnabled ?? true);
  const [pickupEnabled, setPickupEnabled] = useState(tenant?.config?.ecommerce?.pickupEnabled ?? true);
  const [minOrderAmount, setMinOrderAmount] = useState(tenant?.config?.ecommerce?.minOrderAmount ?? 0);
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState(tenant?.config?.ecommerce?.freeDeliveryThreshold ?? 50);

  // Options d'affichage
  const [showVeganBadgeOnCards, setShowVeganBadgeOnCards] = useState(tenant?.config?.ecommerce?.showVeganBadgeOnCards ?? false);

  // ── Horaires de la boutique ──────────────────────────────────────────────
  // La boutique ne prend pas commande en permanence : après le marché les
  // rayons sont vides et l'inventaire n'est pas fait. La fenêtre de commande et
  // les jours de retrait se règlent ici, et sont appliqués côté serveur.
  const [orderingEnabled, setOrderingEnabled] = useState(tenant?.config?.ecommerce?.ordering?.enabled ?? false);
  const [openWeekday, setOpenWeekday] = useState(tenant?.config?.ecommerce?.ordering?.open_weekday ?? 3);
  const [openHour, setOpenHour] = useState(tenant?.config?.ecommerce?.ordering?.open_hour ?? 0);
  const [closeWeekday, setCloseWeekday] = useState(tenant?.config?.ecommerce?.ordering?.close_weekday ?? 6);
  const [closeHour, setCloseHour] = useState(tenant?.config?.ecommerce?.ordering?.close_hour ?? 18);
  const [pickupDays, setPickupDays] = useState<number[]>(tenant?.config?.ecommerce?.pickup?.days ?? []);
  const [pickupSlotsText, setPickupSlotsText] = useState((tenant?.config?.ecommerce?.pickup?.slots ?? []).join('\n'));
  const [pickupLeadDays, setPickupLeadDays] = useState(tenant?.config?.ecommerce?.pickup?.lead_days ?? 1);
  // Jour même (délai 0) : heure limite belge ; vide = toute la journée
  const [sameDayUntilHour, setSameDayUntilHour] = useState<string>(
    tenant?.config?.ecommerce?.pickup?.same_day_until_hour != null ? String(tenant.config.ecommerce.pickup.same_day_until_hour) : '',
  );
  const [pickupMaxDays, setPickupMaxDays] = useState(tenant?.config?.ecommerce?.pickup?.max_days_ahead ?? 21);
  const [closures, setClosures] = useState<Array<{ from: string; to: string; reason?: string }>>(
    tenant?.config?.ecommerce?.closures ?? [],
  );
  const [voiceAssistEnabled, setVoiceAssistEnabled] = useState(tenant?.config?.ecommerce?.voiceAssistEnabled ?? false);
  // Lettre d'actualités : résumé hebdo (à l'ouverture de la fenêtre de commande) des publications pas encore envoyées à la main
  const [weeklyDigest, setWeeklyDigest] = useState(tenant?.config?.ecommerce?.newsletter?.weeklyDigest ?? true);
  // Contrôle visuel des F&L à la préparation (webcam de la balance), 12/10/2026
  const cvInitial = (tenant?.config?.ecommerce as any)?.preparation?.controleVisuel ?? {};
  const [cvActif, setCvActif] = useState<boolean>(cvInitial.actif ?? true);
  const [cvObservation, setCvObservation] = useState<boolean>(cvInitial.observation ?? true);
  const [cvBloquer, setCvBloquer] = useState<boolean>(cvInitial.bloquerSurAlerte ?? false);
  const [cvConservation, setCvConservation] = useState<number>(cvInitial.conservationJours ?? 15);
  // Emails du magasin prévenus à chaque commande eshop payée (vide = adresse de contact)
  const [orderNotificationEmails, setOrderNotificationEmails] = useState(
    (tenant?.config?.ecommerce?.orderNotificationEmails ?? []).join(', '),
  );
  // Rayons commandables même hors fenêtre : le frais attend l'inventaire, pas
  // l'épicerie. (Les précommandes le sont toujours, sans réglage.)
  const [alwaysOpenCategoryIds, setAlwaysOpenCategoryIds] = useState<string[]>(
    tenant?.config?.ecommerce?.ordering?.always_open_category_ids ?? [],
  );
  const [rayons, setRayons] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    api
      .getCategories()
      .then((cats: Array<{ id: string; name: string; parent_id?: string | null }>) =>
        setRayons(
          (cats || [])
            .filter(c => !c.parent_id)
            .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
        ),
      )
      // Sans la liste, la section reste vide : ce n'est pas bloquant pour le reste.
      .catch(() => setRayons([]));
  }, []);

  useEffect(() => {
    setEcommerceEnabled(tenant?.config?.ecommerce?.enabled ?? true);
    setStoreName(tenant?.config?.ecommerce?.storeName || tenant?.name || '');
    setStoreDescription(tenant?.config?.ecommerce?.storeDescription || '');
    setPrimaryColor(tenant?.config?.ecommerce?.primaryColor || '#3B82F6');
    setSecondaryColor(tenant?.config?.ecommerce?.secondaryColor || '#10B981');
    setAccentColor(tenant?.config?.ecommerce?.accentColor || '#F59E0B');
    setLogoPreview(tenant?.config?.ecommerce?.logo || null);
    setDeliveryEnabled(tenant?.config?.ecommerce?.deliveryEnabled ?? true);
    setPickupEnabled(tenant?.config?.ecommerce?.pickupEnabled ?? true);
    setMinOrderAmount(tenant?.config?.ecommerce?.minOrderAmount ?? 0);
    setFreeDeliveryThreshold(tenant?.config?.ecommerce?.freeDeliveryThreshold ?? 50);
    setShowVeganBadgeOnCards(tenant?.config?.ecommerce?.showVeganBadgeOnCards ?? false);
    setOrderingEnabled(tenant?.config?.ecommerce?.ordering?.enabled ?? false);
    setOpenWeekday(tenant?.config?.ecommerce?.ordering?.open_weekday ?? 3);
    setOpenHour(tenant?.config?.ecommerce?.ordering?.open_hour ?? 0);
    setCloseWeekday(tenant?.config?.ecommerce?.ordering?.close_weekday ?? 6);
    setCloseHour(tenant?.config?.ecommerce?.ordering?.close_hour ?? 18);
    setPickupDays(tenant?.config?.ecommerce?.pickup?.days ?? []);
    setPickupSlotsText((tenant?.config?.ecommerce?.pickup?.slots ?? []).join('\n'));
    setPickupLeadDays(tenant?.config?.ecommerce?.pickup?.lead_days ?? 1);
    setSameDayUntilHour(tenant?.config?.ecommerce?.pickup?.same_day_until_hour != null ? String(tenant.config.ecommerce.pickup.same_day_until_hour) : '');
    setPickupMaxDays(tenant?.config?.ecommerce?.pickup?.max_days_ahead ?? 21);
    setClosures(tenant?.config?.ecommerce?.closures ?? []);
    setVoiceAssistEnabled(tenant?.config?.ecommerce?.voiceAssistEnabled ?? false);
    setWeeklyDigest(tenant?.config?.ecommerce?.newsletter?.weeklyDigest ?? true);
    {
      const cv = (tenant?.config?.ecommerce as any)?.preparation?.controleVisuel ?? {};
      setCvActif(cv.actif ?? true);
      setCvObservation(cv.observation ?? true);
      setCvBloquer(cv.bloquerSurAlerte ?? false);
      setCvConservation(cv.conservationJours ?? 15);
    }
    setOrderNotificationEmails((tenant?.config?.ecommerce?.orderNotificationEmails ?? []).join(', '));
    setAlwaysOpenCategoryIds(tenant?.config?.ecommerce?.ordering?.always_open_category_ids ?? []);
  }, [tenant]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setSaveMessage({ type: 'error', text: 'Le fichier est trop volumineux (max 2MB)' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSaveMessage({ type: 'error', text: 'Le fichier doit être une image (PNG, JPG, SVG)' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSave = async () => {
    if (!tenant?.id) {
      setSaveMessage({ type: 'error', text: 'Erreur: Aucun tenant sélectionné' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      let logoUrl = logoPreview;
      if (logoFile) {
        setUploadingLogo(true);
        const reader = new FileReader();
        logoUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(logoFile);
        });
        setUploadingLogo(false);
      }

      const config = {
        ecommerce: {
          enabled: ecommerceEnabled,
          storeName,
          storeDescription,
          primaryColor,
          secondaryColor,
          accentColor,
          logo: logoUrl,
          deliveryEnabled,
          pickupEnabled,
          minOrderAmount,
          freeDeliveryThreshold,
          showVeganBadgeOnCards,
          voiceAssistEnabled,
          newsletter: { weeklyDigest },
          // Les seuils (non éditables ici) sont conservés tels quels.
          preparation: {
            ...((tenant?.config?.ecommerce as any)?.preparation ?? {}),
            controleVisuel: {
              ...((tenant?.config?.ecommerce as any)?.preparation?.controleVisuel ?? {}),
              actif: cvActif,
              observation: cvObservation,
              bloquerSurAlerte: cvBloquer,
              conservationJours: Math.min(365, Math.max(1, Math.round(cvConservation || 15))),
            },
          },
          orderNotificationEmails: orderNotificationEmails
            .split(/[,;\s]+/)
            .map((e) => e.trim().toLowerCase())
            .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)),
          ordering: {
            enabled: orderingEnabled,
            open_weekday: openWeekday,
            open_hour: openHour,
            close_weekday: closeWeekday,
            close_hour: closeHour,
            always_open_category_ids: alwaysOpenCategoryIds,
          },
          pickup: {
            days: pickupDays,
            slots: pickupSlotsText
              .split('\n')
              .map(l => l.trim())
              .filter(Boolean),
            lead_days: pickupLeadDays,
            same_day_until_hour: sameDayUntilHour.trim() === '' ? null : Math.min(23, Math.max(0, Number(sameDayUntilHour))),
            max_days_ahead: pickupMaxDays,
          },
          // Fermetures : les lignes incomplètes sont écartées plutôt que
          // sauvegardées à moitié — une borne vide bloquerait tout ou rien.
          closures: closures.filter(c => c.from && c.to),
        },
      };

      await api.updateTenantConfig(tenant.id, config);
      setSaveMessage({ type: 'success', text: 'Configuration e-commerce sauvegardée !' });
      setLogoFile(null);
      await reloadTenant();
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('[EcommerceSettings] Erreur:', error);
      setSaveMessage({
        type: 'error',
        text: `Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    } finally {
      setSaving(false);
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Activation E-commerce + Accès au site */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">🏪</span>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Boutique E-commerce
                </h3>
                <p className="text-sm text-gray-600">
                  {ecommerceEnabled ? 'Votre boutique est en ligne' : 'Boutique désactivée'}
                </p>
              </div>
            </div>

            {ecommerceEnabled && (
              <div className="flex gap-3 mt-4">
                <a
                  href="/shop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2 shadow-lg"
                >
                  🛍️ Voir la boutique client
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.origin + '/shop');
                    setSaveMessage({ type: 'success', text: 'URL copiée !' });
                    setTimeout(() => setSaveMessage(null), 2000);
                  }}
                  className="px-6 py-3 bg-white text-blue-700 border-2 border-blue-300 rounded-lg hover:bg-blue-50 font-medium transition-colors"
                >
                  📋 Copier le lien
                </button>
              </div>
            )}
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={ecommerceEnabled}
              onChange={(e) => setEcommerceEnabled(e.target.checked)}
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {ecommerceEnabled && (
        <>
          {/* Identité de la boutique */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Identité de la boutique
            </h3>

            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la boutique *
                </label>
                <input
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ma Boutique Bio"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description courte
                </label>
                <textarea
                  rows={3}
                  value={storeDescription}
                  onChange={(e) => setStoreDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Découvrez nos produits bio et locaux sélectionnés avec soin..."
                />
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo de la boutique
                </label>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {logoPreview ? (
                      <div className="w-24 h-24 rounded-lg border-2 border-gray-300 overflow-hidden bg-white">
                        <img
                          src={logoPreview}
                          alt="Logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-xs font-bold"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="flex-1">
                    <input
                      id="ecommerce-logo-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('ecommerce-logo-upload')?.click()}
                      disabled={uploadingLogo}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
                    >
                      {uploadingLogo ? '⏳ Chargement...' : logoPreview ? 'Changer' : 'Choisir une image'}
                    </button>
                    <p className="mt-2 text-xs text-gray-500">
                      PNG, JPG ou SVG. Max 2 MB. Recommandé : 400x400px
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Couleurs */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Palette de couleurs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur principale
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur secondaire
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur d'accent
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Options d'affichage */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Options d'affichage
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Badge végan sur les cartes produits</p>
                  <p className="text-sm text-gray-500">
                    Afficher la pastille « VEGAN » sur les vignettes produits (par défaut, uniquement dans les détails)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showVeganBadgeOnCards}
                    onChange={(e) => setShowVeganBadgeOnCards(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Options de livraison */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Options de livraison
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Livraison à domicile</p>
                  <p className="text-sm text-gray-500">
                    Permettre aux clients de se faire livrer
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={deliveryEnabled}
                    onChange={(e) => setDeliveryEnabled(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Retrait en magasin</p>
                  <p className="text-sm text-gray-500">
                    Permettre aux clients de récupérer leur commande sur place
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={pickupEnabled}
                    onChange={(e) => setPickupEnabled(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant minimum de commande (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={minOrderAmount}
                    onChange={(e) => setMinOrderAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seuil livraison gratuite (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={freeDeliveryThreshold}
                    onChange={(e) => setFreeDeliveryThreshold(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Horaires de la boutique ────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <h3 className="text-lg font-bold text-gray-900">🕒 Horaires de la boutique en ligne</h3>
          <p className="text-sm text-gray-600 mt-1">
            Après le marché les rayons sont vides et l'inventaire n'est pas fait : la boutique ne
            prend commande que sur une plage de la semaine, et les retraits n'ont lieu que les jours
            d'ouverture. Ces règles sont affichées au client <em>et</em> vérifiées à la commande.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">Limiter les jours de commande</div>
            <p className="text-sm text-gray-600">Désactivé, la boutique prend commande en permanence.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={orderingEnabled}
              onChange={e => setOrderingEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {orderingEnabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ouverture</label>
              <select
                value={openWeekday}
                onChange={e => setOpenWeekday(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {JOURS.map((j, i) => (
                  <option key={i} value={i}>{j}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">à</label>
              <select
                value={openHour}
                onChange={e => setOpenHour(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {HEURES.map(h => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fermeture</label>
              <select
                value={closeWeekday}
                onChange={e => setCloseWeekday(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {JOURS.map((j, i) => (
                  <option key={i} value={i}>{j}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">à</label>
              <select
                value={closeHour}
                onChange={e => setCloseHour(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {HEURES.map(h => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {orderingEnabled && (
          <div className="pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rayons commandables même hors fenêtre
            </label>
            <p className="text-sm text-gray-600 mb-3">
              La fenêtre protège le <strong>frais</strong>, dont le stock est inconnu entre le
              marché et l'inventaire. L'épicerie, elle, peut se commander n'importe quand. Les
              pains et autres précommandes sont toujours acceptés, sans réglage.
            </p>
            {rayons.length === 0 ? (
              <p className="text-sm text-gray-500">Rayons non chargés.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {rayons.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() =>
                      setAlwaysOpenCategoryIds(prev =>
                        prev.includes(r.id) ? prev.filter(id => id !== r.id) : [...prev, r.id],
                      )
                    }
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                      alwaysOpenCategoryIds.includes(r.id)
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jours de retrait (ouverture magasin ou marché)
          </label>
          <div className="flex flex-wrap gap-2">
            {JOURS.map((j, i) => (
              <button
                key={i}
                type="button"
                onClick={() =>
                  setPickupDays(prev =>
                    prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i].sort(),
                  )
                }
                className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  pickupDays.includes(i)
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {j}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Aucun jour coché = aucune restriction (tous les jours sont proposés).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Créneaux (un par ligne, facultatif)
            </label>
            <textarea
              rows={4}
              value={pickupSlotsText}
              onChange={e => setPickupSlotsText(e.target.value)}
              placeholder={'10:00 - 12:00\n14:00 - 18:00'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Vide : le client choisit un jour et vient après le message lui annonçant que sa
              commande est prête.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Délai minimum (jours)
            </label>
            <input
              type="number"
              min={0}
              max={14}
              value={pickupLeadDays}
              onChange={e => setPickupLeadDays(Number(e.target.value) || 0)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">
              1 = pas de retrait le jour même. 0 = le jour même est proposé (voir l'heure limite).
            </p>
          </div>
          {pickupLeadDays === 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jour même : jusqu'à (heure)
              </label>
              <input
                type="number"
                min={0}
                max={23}
                placeholder="ex. 14"
                value={sameDayUntilHour}
                onChange={e => setSameDayUntilHour(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">
                Heure belge après laquelle « aujourd'hui » n'est plus proposé. Vide = toute la journée
                (jusqu'à la fermeture des commandes).
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Réservation jusqu'à (jours)
            </label>
            <input
              type="number"
              min={1}
              max={90}
              value={pickupMaxDays}
              onChange={e => setPickupMaxDays(Number(e.target.value) || 1)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-medium text-gray-900">Fermetures exceptionnelles</div>
              <p className="text-sm text-gray-600">
                Semaine d'inventaire, congés : ni commande ni retrait sur la période (bornes incluses).
              </p>
            </div>
            <button
              type="button"
              onClick={() => setClosures(prev => [...prev, { from: '', to: '', reason: '' }])}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
            >
              + Ajouter
            </button>
          </div>

          {closures.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune fermeture programmée.</p>
          ) : (
            <div className="space-y-2">
              {closures.map((c, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center">
                  <input
                    type="date"
                    value={c.from}
                    onChange={e =>
                      setClosures(prev => prev.map((x, i) => (i === idx ? { ...x, from: e.target.value } : x)))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="date"
                    value={c.to}
                    onChange={e =>
                      setClosures(prev => prev.map((x, i) => (i === idx ? { ...x, to: e.target.value } : x)))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="text"
                    value={c.reason || ''}
                    placeholder="Motif affiché au client (ex. inventaire)"
                    onChange={e =>
                      setClosures(prev => prev.map((x, i) => (i === idx ? { ...x, reason: e.target.value } : x)))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setClosures(prev => prev.filter((_, i) => i !== idx))}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
                  >
                    Retirer
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <div className="font-medium text-gray-900">Assistance téléphonique pendant la préparation</div>
            <p className="text-sm text-gray-600">
              À n'activer que lorsque la ligne Twilio est raccordée : la case apparaît au checkout et
              l'appel est annoncé dans les CGV.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={voiceAssistEnabled}
              onChange={e => setVoiceAssistEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Alerte commande au magasin */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="font-medium text-gray-900">Emails prévenus à chaque commande eshop</div>
        <p className="text-sm text-gray-600 mb-3">
          Un email par commande <strong>payée</strong> (FOLIES à la validation, carte dès que Stripe
          confirme) : client, articles, date de retrait, précommandes liées. Plusieurs adresses
          séparées par des virgules. Vide = <code>contact@foliesboutik.be</code>.
        </p>
        <input
          type="text"
          inputMode="email"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="contact@foliesboutik.be, philippe@…"
          value={orderNotificationEmails}
          onChange={e => setOrderNotificationEmails(e.target.value)}
        />
      </div>

      {/* Lettre d'actualités */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium text-gray-900">Résumé hebdomadaire des actualités par email</div>
            <p className="text-sm text-gray-600">
              À l'ouverture de la fenêtre de commande (réglée ci-dessus, jamais avant 8 h), les
              publications de la semaine qui n'ont pas encore été envoyées à la main partent en un
              seul email aux abonnés confirmés. Une fermeture exceptionnelle décale l'envoi à la
              réouverture. Rien ne part au moment de « Publier » : le bouton ✉️ du tableau de bord
              envoie une publication tout de suite.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={weeklyDigest}
              onChange={e => setWeeklyDigest(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Contrôle visuel des fruits et légumes à la préparation */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <div>
          <div className="font-medium text-gray-900">Contrôle visuel des fruits et légumes à la préparation</div>
          <p className="text-sm text-gray-600">
            À chaque pesée validée, la caméra de la balance vérifie que l'article ressemble au produit commandé.
            Contrôle qualité des préparations — aucune statistique par préparateur.
          </p>
        </div>
        {([
          ['Activé', cvActif, setCvActif, 'Photo et vérification à chaque pesée validée.'],
          ['Période d’observation', cvObservation, setCvObservation, 'Tout est mesuré et enregistré, mais rien n’est affiché au préparateur (calage des seuils). À couper après deux semaines.'],
          ['Bloquer sur alerte', cvBloquer, setCvBloquer, 'Une alerte impose de reprendre la photo ; sinon le préparateur peut confirmer « c’est bien le bon produit ».'],
        ] as const).map(([titre, valeur, poser, aide]) => (
          <label key={titre} className="flex items-start justify-between gap-4 cursor-pointer">
            <span>
              <span className="block text-sm font-medium text-gray-900">{titre}</span>
              <span className="block text-xs text-gray-600">{aide}</span>
            </span>
            <input type="checkbox" className="mt-1 h-5 w-5" checked={valeur} onChange={(e) => poser(e.target.checked)} />
          </label>
        ))}
        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-gray-900">Conservation des photos (jours)</span>
            <span className="block text-xs text-gray-600">Délai de contestation fixé par les CGV ; les photos plus anciennes sont effacées chaque nuit.</span>
          </span>
          <input id="cv-conservation" type="number" min={1} max={365} value={cvConservation}
            onChange={(e) => setCvConservation(Number(e.target.value))}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-right" />
        </label>
      </div>

      {/* Message et bouton de sauvegarde */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t-2 border-gray-200">
        {saveMessage && (
          <div
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              saveMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {saveMessage.type === 'success' ? '✓ ' : '⚠️ '}
            {saveMessage.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les modifications'}
        </button>
      </div>
    </div>
  );
}
